// PATH: src/components/AppLayout.jsx
// VERSION: 1.4 — Fix scroll en móvil (minH 100vh, sin overflowY en main)

import React, { useMemo, useState, useEffect } from 'react';
import { Box, useBreakpointValue, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, useDisclosure, useColorModeValue } from '@chakra-ui/react';
import SideNav from './SideNav';
import TopBar from './TopBar';
import Footer from './Footer';
import BottomNav from './BottomNav';
import GlobalAudioBar from './common/GlobalAudioBar';
import { useLocation } from 'react-router-dom';

const COLLAPSED_W = 72;
const EXPANDED_W = 260;

export default function AppLayout({ children }) {
  const collapseByDefault = useBreakpointValue({ base: true, md: false });
  const [isCollapsed, setIsCollapsed] = useState(collapseByDefault ?? true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const drawerBg = useColorModeValue('white', 'gray.900');
  const drawerColor = useColorModeValue('brand.800', 'white');
  const drawerBorder = useColorModeValue('gray.200', 'brand.700');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navWidth = useMemo(
    () => (isCollapsed ? COLLAPSED_W : EXPANDED_W),
    [isCollapsed]
  );

  return (
    <Box minH="100vh" bg="brand.900" pb={{ base: 'env(safe-area-inset-bottom)', md: 0 }}>
      {/* SideNav se asume position: fixed internamente */}
      <SideNav
        isCollapsed={isCollapsed}
        toggleCollapse={() => setIsCollapsed(v => !v)}
        width={navWidth}
        collapsedWidth={COLLAPSED_W}
        expandedWidth={EXPANDED_W}
      />

      <Box
        pl={{ base: 0, md: `${navWidth}px` }}
        pb={{ base: '64px', md: 0 }} // Espacio para el BottomNav en móvil
        minH="100vh"
        display="flex"
        flexDir="column"
        bg="bg.default"
      >
        {/* TopBar */}
        <Box
          as="header"
          position="sticky"
          top="0"
          zIndex="1000"
          sx={{
            paddingTop: 'env(safe-area-inset-top)', // evita que la barra del navegador tape el header
          }}
        >
          <TopBar />
        </Box>

        {/* Contenido principal — deja que el body haga el scroll */}
        <Box
          as="main"
          flex="1 0 auto"
          px={{ base: 4, md: 6 }}
          py={{ base: 6, md: 8 }}
        >
          {children}
        </Box>

        {/* Footer fijo visualmente, pero dejando que la página crezca en alto */}
        <Box
          as="footer"
          flexShrink={0}
          sx={{
            paddingBottom: { base: 0, md: 'env(safe-area-inset-bottom)' },
          }}
        >
          <Footer />
        </Box>
      </Box>

      {/* Navegación Inferior en Móvil */}
      <BottomNav onOpenMenu={onOpen} />

      {/* Barra de Audio Global */}
      <GlobalAudioBar />

      {/* Drawer del Menú Móvil */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg={drawerBg} color={drawerColor}>
          <DrawerCloseButton mt={2} />
          <DrawerHeader borderBottomWidth="1px" borderColor={drawerBorder}>Menú</DrawerHeader>
          <DrawerBody p={0}>
            {/* Reusamos SideNav en modo móvil pero con props especiales o simplemente inyectando su contenido */}
            <SideNav isMobile onCloseDrawer={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
